if (!customElements.get('watch-configurator')) {
  customElements.define(
    'watch-configurator',
    class WatchConfigurator extends HTMLElement {
      connectedCallback() {
        this.canvas = this.querySelector('[data-watch-canvas]');
        this.canvasConfig = this.querySelector('[data-watch-canvas-config]') || this.canvas;
        this.rowsContainer = this.querySelector('[data-watch-rows]');
        this.extraListContainer = this.querySelector('[data-watch-extra-list]');
        this.addWatchButton = this.querySelector('[data-watch-add-row]');
        this.builderRowsInput = this.querySelector('[data-watch-builder-rows]');
        this.extraStrapsInput = this.querySelector('[data-watch-extra-straps]');
        this.estimateValue = this.querySelector('[data-watch-estimate-value]');

        this.decreaseLabel = this.dataset.decreaseLabel || 'Decrease quantity';
        this.increaseLabel = this.dataset.increaseLabel || 'Increase quantity';
        this.maxWatchRows = this.parsePositiveInt(this.dataset.maxWatchRows, 6);
        this.maxExtraQty = this.parsePositiveInt(this.dataset.maxExtraQty, 10);
        this.rowTitleTemplate = this.dataset.rowTitleTemplate || 'Watch __index__';
        this.modeSelectorLabel = this.dataset.modeSelectorLabel || 'Select option';
        this.modeTabTimeLabel = this.dataset.modeTabTimeLabel || 'Time';
        this.modeTabFunLabel = this.dataset.modeTabFunLabel || 'Fun';
        this.modeTabRandomLabel = this.dataset.modeTabRandomLabel || 'Random';
        this.modeTabTextLabel = this.dataset.modeTabTextLabel || 'Text';
        this.rowTimeLabel = this.dataset.rowTimeLabel || 'Select time';
        this.rowTextLabel = this.dataset.rowTextLabel || 'Text (max 6 letters)';
        this.funTimesLabel = this.dataset.funTimesLabel || 'Fun times';
        this.funTimesPlaceholder = this.dataset.funTimesPlaceholder || 'Pick a time';
        this.randomTimeButtonLabel = this.dataset.randomTimeButtonLabel || 'Generate';
        this.randomTimeResultPrefix = this.dataset.randomTimeResultPrefix || 'Generated time:';
        this.removeWatchLabel = this.dataset.removeWatchLabel || 'Remove watch';

        this.variantPrices = this.parseJsonDataset(this.dataset.variantPrices, {});
        this.strapOptions = this.parseJsonDataset(this.dataset.strapOptions, []).filter((option) =>
          Number.isInteger(this.parsePositiveInt(option?.id, 0))
        );

        this.currencyCode = this.dataset.currencyCode || 'EUR';
        this.previewMode = (this.dataset.previewMode || 'full').toLowerCase();
        this.minuteStep = Math.max(1, this.parsePositiveInt(this.dataset.minuteStep, 1));

        this.headBasePriceFallbackCents = this.parseCurrencyToCents(this.dataset.headBasePrice, 1500);
        this.headSecondPriceCents = this.parseCurrencyToCents(this.dataset.headSecondPrice, 750);
        this.headThirdPriceCents = this.parseCurrencyToCents(this.dataset.headThirdPrice, 500);
        this.headFourthPlusPriceCents = this.parseCurrencyToCents(this.dataset.headFourthPlusPrice, 400);
        this.strapFirstPriceCents = this.parseCurrencyToCents(this.dataset.strapFirstPrice, 1000);
        this.strapSecondPlusPriceCents = this.parseCurrencyToCents(this.dataset.strapSecondPlusPrice, 500);
        this.dialTextSizePercent = this.parsePositiveFloat(this.dataset.dialTextSizePercent, 21);

        this.defaultTime = this.normalizeTime(this.dataset.defaultTime || '11:35');
        this.currentTime = this.defaultTime;
        this.rowCounter = 0;
        this.activeRowId = null;
        this.funTimes = this.getDefaultFunTimes();

        this.strapPriceByVariantId = {};
        this.strapOptions.forEach((option) => {
          const variantId = this.parsePositiveInt(option.id, 0);
          const priceCents = this.parsePositiveInt(option.price, 0);
          if (!variantId) return;
          this.strapPriceByVariantId[variantId] = priceCents;
        });

        const formId = this.dataset.productFormId;
        this.productForm = formId ? document.getElementById(formId) : null;
        this.variantIdInput = this.productForm?.querySelector('input[name="id"]') || null;

        if (this.strapOptions.length > 0) {
          this.renderExtraOptions();
          this.ensureAtLeastOneRow();
        }

        this.bindEvents();
        this.initDialImage();
        this.syncAll();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.boundResizeHandler);
        this.removeEventListener('click', this.boundClickHandler);
        this.removeEventListener('change', this.boundChangeHandler);
        this.removeEventListener('input', this.boundInputHandler);
        this.variantIdInput?.removeEventListener('change', this.boundVariantChangeHandler);
      }

      bindEvents() {
        this.boundResizeHandler = this.drawPreview.bind(this);
        this.boundClickHandler = this.handleClick.bind(this);
        this.boundChangeHandler = this.handleChange.bind(this);
        this.boundInputHandler = this.handleInput.bind(this);
        this.boundVariantChangeHandler = this.syncAll.bind(this);

        window.addEventListener('resize', this.boundResizeHandler);
        this.addEventListener('click', this.boundClickHandler);
        this.addEventListener('change', this.boundChangeHandler);
        this.addEventListener('input', this.boundInputHandler);
        this.variantIdInput?.addEventListener('change', this.boundVariantChangeHandler);
      }

      parseJsonDataset(rawValue, fallbackValue) {
        if (!rawValue) return fallbackValue;

        try {
          return JSON.parse(rawValue);
        } catch (error) {
          console.error('Invalid watch configurator JSON dataset', error);
          return fallbackValue;
        }
      }

      parsePositiveInt(rawValue, fallbackValue) {
        const parsed = Number.parseInt(String(rawValue ?? ''), 10);
        if (Number.isNaN(parsed) || parsed < 0) return fallbackValue;
        return parsed;
      }

      parsePositiveFloat(rawValue, fallbackValue) {
        const normalized = String(rawValue ?? '')
          .replace(',', '.')
          .trim();
        const parsed = Number.parseFloat(normalized);
        if (Number.isNaN(parsed) || parsed <= 0) return fallbackValue;
        return parsed;
      }

      parseCurrencyToCents(rawValue, fallbackValue) {
        const normalized = String(rawValue ?? '')
          .replace(',', '.')
          .trim();
        const floatValue = Number.parseFloat(normalized);

        if (Number.isNaN(floatValue)) return fallbackValue;
        return Math.round(floatValue * 100);
      }

      normalizeTime(rawTime) {
        const value = String(rawTime || '').trim();
        const match = value.match(/^(\d{1,2}):(\d{1,2})$/);

        if (!match) return this.defaultTime || '11:35';

        const hours = Math.min(23, Math.max(0, Number.parseInt(match[1], 10)));
        const minutes = Math.min(59, Math.max(0, Number.parseInt(match[2], 10)));

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      }

      getDefaultFunTimes() {
        const candidates = [
          { value: '6:7', label: '6:7 - six seven in 2026...' },
          { value: '6:9', label: '6:9 - nice' },
          { value: '4:20', label: '4:20 - blaze it' },
          { value: '13:37', label: '13:37 - leet hacker' },
          { value: '11:11', label: '11:11 - bro è un segno' },
        ];
        return Array.from(
          new Set(
            candidates
              .map((item) => {
                const normalizedValue = this.normalizeTime(item.value);
                return JSON.stringify({
                  value: normalizedValue,
                  label: item.label,
                });
              })
              .filter((entry) => {
                const parsedEntry = JSON.parse(entry);
                return /^\d{2}:\d{2}$/.test(parsedEntry.value);
              })
          )
        ).map((entry) => JSON.parse(entry));
      }

      getRandomTime() {
        const minuteStep = Math.max(1, this.minuteStep);
        const stepsInDay = Math.floor((24 * 60) / minuteStep);
        const randomStep = Math.floor(Math.random() * stepsInDay);
        const totalMinutes = randomStep * minuteStep;
        const hours = Math.floor(totalMinutes / 60) % 24;
        const minutes = totalMinutes % 60;
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      }

      normalizeDialText(rawText) {
        const normalized = String(rawText || '')
          .normalize('NFC')
          .replace(/[^A-Za-zÀ-ÖØ-öø-ÿ]/g, '')
          .slice(0, 6);
        return normalized;
      }

      initDialImage() {
        const dialSrc = this.canvasConfig?.dataset.dialSrc;

        if (!dialSrc) {
          this.dialImage = null;
          this.drawPreview();
          return;
        }

        this.dialImage = new Image();
        this.dialImage.onload = () => this.drawPreview();
        this.dialImage.onerror = () => {
          this.dialImage = null;
          this.drawPreview();
        };
        this.dialImage.src = dialSrc;
      }

      handleClick(event) {
        const addRowButton = event.target.closest('[data-watch-add-row]');
        if (addRowButton) {
          event.preventDefault();
          this.addRow(true, true);
          return;
        }

        const removeRowButton = event.target.closest('[data-watch-remove-row]');
        if (removeRowButton) {
          event.preventDefault();
          this.removeRow(removeRowButton.dataset.watchRemoveRow);
          return;
        }

        const actionButton = event.target.closest('[data-qty-action]');
        if (actionButton) {
          const targetInput = this.querySelector(`#${actionButton.dataset.qtyTarget}`);
          if (!targetInput) return;

          event.preventDefault();
          const step = 1;
          const min = this.parsePositiveInt(targetInput.min, 0);
          const max = this.parsePositiveInt(targetInput.max, this.maxExtraQty);
          const currentValue = this.parsePositiveInt(targetInput.value, min);

          if (actionButton.dataset.qtyAction === 'plus') {
            targetInput.value = String(Math.min(max, currentValue + step));
          } else {
            targetInput.value = String(Math.max(min, currentValue - step));
          }

          targetInput.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        const randomTimeButton = event.target.closest('[data-watch-random-time]');
        if (randomTimeButton) {
          event.preventDefault();
          const rowElement = randomTimeButton.closest('[data-watch-row]');
          if (!rowElement) return;

          const rowTimeInput = rowElement.querySelector('[data-watch-row-time]');
          if (!rowTimeInput) return;

          const generatedTime = this.getRandomTime();
          rowTimeInput.value = generatedTime;

          const randomResult = rowElement.querySelector('[data-watch-random-time-result]');
          if (randomResult) {
            randomResult.textContent = `${this.randomTimeResultPrefix} ${generatedTime}`;
            randomResult.hidden = false;
          }

          this.setRowMode(rowElement, 'random');
          this.setActiveRow(rowElement.dataset.rowId);
          this.syncRowsPayload();
          this.updateEstimate();
          this.drawPreview();
          return;
        }

        const rowElement = event.target.closest('[data-watch-row]');
        if (rowElement) {
          this.setActiveRow(rowElement.dataset.rowId);
          this.drawPreview();
        }
      }

      handleInput(event) {
        if (event.target.matches('[data-watch-row-time]')) {
          const input = event.target;
          input.value = this.normalizeTime(input.value);
          const rowElement = input.closest('[data-watch-row]');
          if (rowElement) {
            this.setRowMode(rowElement, 'time');
            this.setActiveRow(rowElement.dataset.rowId);
          }
          this.syncRowsPayload();
          this.updateEstimate();
          this.drawPreview();
          return;
        }

        if (event.target.matches('[data-watch-row-text]')) {
          const input = event.target;
          input.value = this.normalizeDialText(input.value);
          const rowElement = input.closest('[data-watch-row]');
          if (rowElement) {
            this.setRowMode(rowElement, 'text');
            this.setActiveRow(rowElement.dataset.rowId);
          }
          this.syncRowsPayload();
          this.drawPreview();
          return;
        }

        if (event.target.matches('[data-watch-extra-qty]')) {
          this.normalizeExtraQuantityInput(event.target);
          this.syncExtraPayload();
          this.updateEstimate();
        }
      }

      handleChange(event) {
        if (event.target.matches('[data-watch-mode-select]')) {
          const rowElement = event.target.closest('[data-watch-row]');
          if (!rowElement) return;

          const selectedMode = String(event.target.value || 'time').trim();
          this.setRowMode(rowElement, selectedMode);
          this.setActiveRow(rowElement.dataset.rowId);
          this.syncRowsPayload();
          this.drawPreview();
          return;
        }

        if (event.target.matches('[data-watch-fun-time]')) {
          const rawSelectedTime = String(event.target.value || '').trim();
          if (!rawSelectedTime) return;
          const selectedTime = this.normalizeTime(rawSelectedTime);

          const rowElement = event.target.closest('[data-watch-row]');
          if (!rowElement) return;

          const rowTimeInput = rowElement.querySelector('[data-watch-row-time]');
          if (!rowTimeInput) return;

          rowTimeInput.value = selectedTime;
          this.setRowMode(rowElement, 'fun');
          this.setActiveRow(rowElement.dataset.rowId);
          this.syncRowsPayload();
          this.updateEstimate();
          this.drawPreview();
          return;
        }

        if (event.target.matches('[data-watch-row-strap]')) {
          const rowElement = event.target.closest('[data-watch-row]');
          if (rowElement) {
            this.setActiveRow(rowElement.dataset.rowId);
            this.updateRowOptionState(rowElement);
          }
          this.syncRowsPayload();
          this.updateEstimate();
          return;
        }

        if (event.target.matches('[data-watch-row-time]')) {
          event.target.value = this.normalizeTime(event.target.value);
          const rowElement = event.target.closest('[data-watch-row]');
          if (rowElement) this.setRowMode(rowElement, 'time');
          this.syncRowsPayload();
          this.updateEstimate();
          this.drawPreview();
          return;
        }

        if (event.target.matches('[data-watch-row-text]')) {
          event.target.value = this.normalizeDialText(event.target.value);
          const rowElement = event.target.closest('[data-watch-row]');
          if (rowElement) this.setRowMode(rowElement, 'text');
          this.syncRowsPayload();
          this.drawPreview();
          return;
        }

        if (event.target.matches('[data-watch-extra-qty]')) {
          this.normalizeExtraQuantityInput(event.target);
          this.syncExtraPayload();
          this.updateEstimate();
        }
      }

      normalizeExtraQuantityInput(input) {
        const min = this.parsePositiveInt(input.min, 0);
        const max = this.parsePositiveInt(input.max, this.maxExtraQty);
        const value = this.parsePositiveInt(input.value, min);
        input.value = String(Math.min(max, Math.max(min, value)));
      }

      ensureAtLeastOneRow() {
        if (!this.rowsContainer) return;
        const rowCount = this.getRowElements().length;
        if (rowCount === 0 && this.strapOptions.length > 0) {
          this.addRow(true, false);
        }
      }

      addRow(selectRow = true, syncAfter = true) {
        if (!this.rowsContainer || this.strapOptions.length === 0) return null;

        const existingRows = this.getRowElements();
        if (existingRows.length >= this.maxWatchRows) return null;
        const isFirstRow = existingRows.length === 0;

        const rowId = String(this.rowCounter);
        this.rowCounter += 1;

        const rowElement = this.buildRowElement(rowId, isFirstRow);
        this.rowsContainer.appendChild(rowElement);

        if (selectRow) {
          this.setActiveRow(rowId);
        }

        this.updateRowsMeta();
        if (syncAfter) this.syncAll();
        return rowElement;
      }

      removeRow(rowId) {
        const rows = this.getRowElements();
        if (rows.length <= 1) return;

        const rowToRemove = this.rowsContainer?.querySelector(`[data-row-id="${rowId}"]`);
        if (!rowToRemove) return;

        rowToRemove.remove();

        const nextRows = this.getRowElements();
        if (!nextRows.length) {
          this.activeRowId = null;
        } else if (this.activeRowId === rowId) {
          this.setActiveRow(nextRows[0].dataset.rowId);
        }

        this.updateRowsMeta();
        this.syncAll();
      }

      buildRowElement(rowId, requireStrapSelection = false) {
        const rowElement = document.createElement('article');
        rowElement.className = 'watch-configurator__watch-row';
        rowElement.dataset.watchRow = '';
        rowElement.dataset.rowId = rowId;

        const header = document.createElement('div');
        header.className = 'watch-configurator__watch-row-header';

        const rowMeta = document.createElement('div');
        rowMeta.className = 'watch-configurator__watch-row-meta';
        rowMeta.dataset.watchRowMeta = '';

        const rowMain = document.createElement('div');
        rowMain.className = 'watch-configurator__watch-row-main';

        const previewCanvas = document.createElement('canvas');
        previewCanvas.className = 'watch-configurator__watch-row-preview';
        previewCanvas.width = 128;
        previewCanvas.height = 128;
        previewCanvas.dataset.watchRowCanvas = '';
        previewCanvas.setAttribute('aria-hidden', 'true');

        const headPrice = document.createElement('p');
        headPrice.className = 'watch-configurator__watch-row-price';
        headPrice.dataset.watchRowHeadPrice = '';

        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.className = 'watch-configurator__remove-row';
        removeButton.dataset.watchRemoveRow = rowId;
        removeButton.setAttribute('aria-label', this.removeWatchLabel);
        removeButton.setAttribute('title', this.removeWatchLabel);
        removeButton.innerHTML = '<span aria-hidden="true">\u00D7</span>';

        rowMain.appendChild(previewCanvas);
        rowMeta.appendChild(headPrice);
        rowMain.appendChild(rowMeta);
        header.appendChild(rowMain);
        header.appendChild(removeButton);

        const modeSelectorField = document.createElement('label');
        modeSelectorField.className = 'watch-configurator__field watch-configurator__field--mode-select';

        const modeSelectorLabel = document.createElement('span');
        modeSelectorLabel.className = 'form__label';
        modeSelectorLabel.textContent = this.modeSelectorLabel;

        const modeSelector = document.createElement('select');
        modeSelector.className = 'watch-configurator__mode-select-input';
        modeSelector.dataset.watchModeSelect = '';

        const modeEntries = [
          { mode: 'time', label: this.modeTabTimeLabel },
          { mode: 'fun', label: this.modeTabFunLabel },
          { mode: 'random', label: this.modeTabRandomLabel },
          { mode: 'text', label: this.modeTabTextLabel },
        ];
        modeEntries.forEach((entry) => {
          const option = document.createElement('option');
          option.value = entry.mode;
          option.textContent = entry.label;
          modeSelector.appendChild(option);
        });

        modeSelectorField.appendChild(modeSelectorLabel);
        modeSelectorField.appendChild(modeSelector);

        const timeField = document.createElement('label');
        timeField.className = 'watch-configurator__field watch-configurator__field--row-time';

        const timeLabel = document.createElement('span');
        timeLabel.className = 'form__label';
        timeLabel.textContent = this.rowTimeLabel;

        const timeInput = document.createElement('input');
        timeInput.type = 'time';
        timeInput.className = 'watch-configurator__time-input';
        timeInput.dataset.watchRowTime = '';
        timeInput.step = String(this.minuteStep * 60);
        timeInput.value = this.defaultTime;

        timeField.appendChild(timeLabel);
        timeField.appendChild(timeInput);

        const timePanel = document.createElement('div');
        timePanel.className = 'watch-configurator__mode-panel';
        timePanel.dataset.watchModePanel = 'time';
        timePanel.appendChild(timeField);

        const timeTools = document.createElement('div');
        timeTools.className = 'watch-configurator__time-tools';

        const timeToolsLabel = document.createElement('span');
        timeToolsLabel.className = 'watch-configurator__time-tools-label';
        timeToolsLabel.textContent = this.funTimesLabel;

        const timeToolsControls = document.createElement('div');
        timeToolsControls.className = 'watch-configurator__time-tools-controls';

        const funTimesSelect = document.createElement('select');
        funTimesSelect.className = 'watch-configurator__fun-times-select';
        funTimesSelect.dataset.watchFunTime = '';

        const funTimesPlaceholder = document.createElement('option');
        funTimesPlaceholder.value = '';
        funTimesPlaceholder.textContent = this.funTimesPlaceholder;
        funTimesSelect.appendChild(funTimesPlaceholder);

        this.funTimes.forEach((timeItem) => {
          const option = document.createElement('option');
          option.value = timeItem.value;
          option.textContent = timeItem.label;
          funTimesSelect.appendChild(option);
        });

        const funPanel = document.createElement('div');
        funPanel.className = 'watch-configurator__mode-panel';
        funPanel.dataset.watchModePanel = 'fun';

        const randomField = document.createElement('div');
        randomField.className = 'watch-configurator__random-time-wrap';

        const randomTimeButton = document.createElement('button');
        randomTimeButton.type = 'button';
        randomTimeButton.className = 'button button--secondary watch-configurator__random-time-button';
        randomTimeButton.dataset.watchRandomTime = '';
        randomTimeButton.textContent = this.randomTimeButtonLabel;

        const randomTimeResult = document.createElement('p');
        randomTimeResult.className = 'watch-configurator__random-time-result';
        randomTimeResult.dataset.watchRandomTimeResult = '';
        randomTimeResult.hidden = true;

        timeToolsControls.appendChild(funTimesSelect);
        timeTools.appendChild(timeToolsLabel);
        timeTools.appendChild(timeToolsControls);
        funPanel.appendChild(timeTools);

        const randomPanel = document.createElement('div');
        randomPanel.className = 'watch-configurator__mode-panel';
        randomPanel.dataset.watchModePanel = 'random';
        randomField.appendChild(randomTimeButton);
        randomField.appendChild(randomTimeResult);
        randomPanel.appendChild(randomField);

        const textField = document.createElement('label');
        textField.className = 'watch-configurator__field watch-configurator__field--row-text';

        const textLabel = document.createElement('span');
        textLabel.className = 'form__label';
        textLabel.textContent = this.rowTextLabel;

        const textInput = document.createElement('input');
        textInput.type = 'text';
        textInput.className = 'watch-configurator__text-input';
        textInput.dataset.watchRowText = '';
        textInput.maxLength = 6;
        textInput.autocomplete = 'off';
        textInput.value = '';

        textField.appendChild(textLabel);
        textField.appendChild(textInput);

        const textPanel = document.createElement('div');
        textPanel.className = 'watch-configurator__mode-panel';
        textPanel.dataset.watchModePanel = 'text';
        textPanel.appendChild(textField);

        const modeLayout = document.createElement('div');
        modeLayout.className = 'watch-configurator__mode-layout';
        modeLayout.appendChild(modeSelectorField);
        modeLayout.appendChild(timePanel);
        modeLayout.appendChild(funPanel);
        modeLayout.appendChild(randomPanel);
        modeLayout.appendChild(textPanel);

        rowElement.appendChild(header);
        rowElement.appendChild(modeLayout);
        this.setRowMode(rowElement, 'time');

        if (requireStrapSelection) {
          const optionsGrid = document.createElement('div');
          optionsGrid.className = 'watch-configurator__strap-options';

          this.strapOptions.forEach((option, optionIndex) => {
            const optionVariantId = String(this.parsePositiveInt(option.id, 0));
            const optionPriceCents = String(this.parsePositiveInt(option.price, 0));

            const optionLabel = document.createElement('label');
            optionLabel.className = 'watch-configurator__strap-option';
            optionLabel.dataset.watchStrapOption = '';

            const radioInput = document.createElement('input');
            radioInput.type = 'radio';
            radioInput.className = 'watch-configurator__strap-radio';
            radioInput.dataset.watchRowStrap = '';
            radioInput.value = optionVariantId;
            radioInput.dataset.variantId = optionVariantId;
            radioInput.dataset.variantPrice = optionPriceCents;
            radioInput.name = `watch-row-strap-${rowId}`;
            radioInput.checked = optionIndex === 0;

            const optionBody = document.createElement('span');
            optionBody.className = 'watch-configurator__strap-option-body';

            if (option.image) {
              const optionImage = document.createElement('img');
              optionImage.className = 'watch-configurator__strap-option-image';
              optionImage.src = option.image;
              optionImage.alt = option.label || '';
              optionImage.width = 44;
              optionImage.height = 44;
              optionImage.loading = 'lazy';
              optionBody.appendChild(optionImage);
            }

            const optionText = document.createElement('span');
            optionText.className = 'watch-configurator__strap-option-text';

            const optionName = document.createElement('span');
            optionName.className = 'watch-configurator__strap-option-label';
            optionName.textContent = option.label || '';

            optionText.appendChild(optionName);
            optionBody.appendChild(optionText);
            optionLabel.appendChild(radioInput);
            optionLabel.appendChild(optionBody);
            optionsGrid.appendChild(optionLabel);
          });

          rowElement.appendChild(optionsGrid);
          this.updateRowOptionState(rowElement);
        }

        return rowElement;
      }

      renderExtraOptions() {
        if (!this.extraListContainer) return;

        this.extraListContainer.innerHTML = '';

        this.strapOptions.forEach((option, optionIndex) => {
          const variantId = String(this.parsePositiveInt(option.id, 0));
          const optionPriceCents = String(this.parsePositiveInt(option.price, 0));
          const inputId = `WatchExtraQty-${this.dataset.productFormId}-${optionIndex}`;

          const row = document.createElement('div');
          row.className = 'watch-configurator__extra-row';

          const meta = document.createElement('div');
          meta.className = 'watch-configurator__extra-meta';

          if (option.image) {
            const image = document.createElement('img');
            image.className = 'watch-configurator__extra-image';
            image.src = option.image;
            image.alt = option.label || '';
            image.width = 38;
            image.height = 38;
            image.loading = 'lazy';
            meta.appendChild(image);
          }

          const title = document.createElement('span');
          title.className = 'watch-configurator__extra-text';

          const name = document.createElement('span');
          name.className = 'watch-configurator__extra-name';
          name.textContent = option.label || '';

          const price = document.createElement('span');
          price.className = 'watch-configurator__extra-price';
          price.dataset.watchExtraPrice = '';
          price.dataset.variantPrice = optionPriceCents;

          title.appendChild(name);
          title.appendChild(price);
          meta.appendChild(title);

          const qty = document.createElement('div');
          qty.className = 'watch-configurator__qty';
          qty.setAttribute('role', 'group');
          qty.setAttribute('aria-label', option.label || 'Extra strap');

          const minusButton = document.createElement('button');
          minusButton.type = 'button';
          minusButton.className = 'watch-configurator__qty-btn';
          minusButton.dataset.qtyAction = 'minus';
          minusButton.dataset.qtyTarget = inputId;
          minusButton.setAttribute('aria-label', this.decreaseLabel);
          minusButton.textContent = '-';

          const qtyInput = document.createElement('input');
          qtyInput.id = inputId;
          qtyInput.type = 'number';
          qtyInput.className = 'watch-configurator__qty-input';
          qtyInput.min = '0';
          qtyInput.max = String(this.maxExtraQty);
          qtyInput.value = '0';
          qtyInput.dataset.watchExtraQty = '';
          qtyInput.dataset.variantId = variantId;
          qtyInput.dataset.variantPrice = optionPriceCents;

          const plusButton = document.createElement('button');
          plusButton.type = 'button';
          plusButton.className = 'watch-configurator__qty-btn';
          plusButton.dataset.qtyAction = 'plus';
          plusButton.dataset.qtyTarget = inputId;
          plusButton.setAttribute('aria-label', this.increaseLabel);
          plusButton.textContent = '+';

          qty.appendChild(minusButton);
          qty.appendChild(qtyInput);
          qty.appendChild(plusButton);

          row.appendChild(meta);
          row.appendChild(qty);
          this.extraListContainer.appendChild(row);
        });
      }

      getRowElements() {
        if (!this.rowsContainer) return [];
        return Array.from(this.rowsContainer.querySelectorAll('[data-watch-row]'));
      }

      updateRowsMeta() {
        const rows = this.getRowElements();
        const canRemoveAny = rows.length > 1;

        rows.forEach((rowElement, rowIndex) => {
          const removeButton = rowElement.querySelector('[data-watch-remove-row]');
          if (!removeButton) return;
          const canRemoveThisRow = canRemoveAny && rowIndex > 0;
          removeButton.disabled = !canRemoveThisRow;
          removeButton.classList.toggle('is-hidden', !canRemoveThisRow);
        });

        if (this.addWatchButton) {
          this.addWatchButton.disabled = rows.length >= this.maxWatchRows;
        }

        this.refreshDisplayedPrices();
      }

      updateRowOptionState(rowElement) {
        const options = Array.from(rowElement.querySelectorAll('[data-watch-strap-option]'));
        options.forEach((optionLabel) => {
          const input = optionLabel.querySelector('[data-watch-row-strap]');
          const isSelected = Boolean(input?.checked);
          optionLabel.classList.toggle('is-selected', isSelected);
        });
      }

      setRowMode(rowElement, mode) {
        if (!rowElement || !mode) return;
        rowElement.dataset.watchSelectedMode = mode;

        const modeSelect = rowElement.querySelector('[data-watch-mode-select]');
        if (modeSelect && modeSelect.value !== mode) {
          modeSelect.value = mode;
        }

        const modePanels = Array.from(rowElement.querySelectorAll('[data-watch-mode-panel]'));
        modePanels.forEach((panelElement) => {
          panelElement.hidden = panelElement.dataset.watchModePanel !== mode;
        });
      }

      setActiveRow(rowId) {
        const rows = this.getRowElements();
        if (!rows.length) {
          this.activeRowId = null;
          this.currentTime = this.defaultTime;
          return;
        }

        const resolvedRowId = rowId || rows[0].dataset.rowId;

        rows.forEach((rowElement) => {
          const isActive = rowElement.dataset.rowId === resolvedRowId;
          rowElement.classList.toggle('is-active', isActive);
        });

        this.activeRowId = resolvedRowId;

        const activeRow = rows.find((rowElement) => rowElement.dataset.rowId === resolvedRowId) || rows[0];
        const timeInput = activeRow.querySelector('[data-watch-row-time]');
        this.currentTime = this.normalizeTime(timeInput?.value || this.defaultTime);
      }

      collectRows() {
        const selectedVariantId = this.parsePositiveInt(this.variantIdInput?.value, 0);

        return this.getRowElements().map((rowElement) => {
          const timeInput = rowElement.querySelector('[data-watch-row-time]');
          const textInput = rowElement.querySelector('[data-watch-row-text]');
          const selectedStrapInput = rowElement.querySelector('[data-watch-row-strap]:checked');

          return {
            headVariantId: selectedVariantId,
            time24h: this.normalizeTime(timeInput?.value || this.defaultTime),
            dialText: this.normalizeDialText(textInput?.value || ''),
            strapVariantId: this.parsePositiveInt(selectedStrapInput?.value, 0),
          };
        });
      }

      collectExtraStraps() {
        return Array.from(this.querySelectorAll('[data-watch-extra-qty]'))
          .map((inputElement) => ({
            variantId: this.parsePositiveInt(inputElement.dataset.variantId, 0),
            quantity: this.parsePositiveInt(inputElement.value, 0),
          }))
          .filter((item) => item.variantId > 0 && item.quantity > 0);
      }

      syncRowsPayload() {
        if (!this.builderRowsInput) return;

        const rows = this.collectRows();
        this.builderRowsInput.value = JSON.stringify(rows);

        if (!this.activeRowId && rows.length) {
          const firstRow = this.getRowElements()[0];
          if (firstRow) this.setActiveRow(firstRow.dataset.rowId);
        }

        this.updateRowsMeta();
      }

      syncExtraPayload() {
        if (!this.extraStrapsInput) return;
        this.extraStrapsInput.value = JSON.stringify(this.collectExtraStraps());
      }

      syncAll() {
        this.syncRowsPayload();
        this.syncExtraPayload();
        this.updateEstimate();
        this.drawPreview();
      }

      getSelectedProductBasePriceCents() {
        const selectedVariantId = this.variantIdInput?.value;

        if (selectedVariantId && this.variantPrices[selectedVariantId] !== undefined) {
          return this.parsePositiveInt(this.variantPrices[selectedVariantId], 0);
        }

        const firstValue = Object.values(this.variantPrices)[0];
        return this.parsePositiveInt(firstValue, 0);
      }

      getMandatoryStrapBasePriceCents() {
        const firstRow = this.getRowElements()[0];
        const selectedFirstRowStrap = firstRow?.querySelector('[data-watch-row-strap]:checked');
        const selectedFirstRowPrice = this.parsePositiveInt(selectedFirstRowStrap?.dataset.variantPrice, 0);
        if (selectedFirstRowPrice > 0) return selectedFirstRowPrice;

        const firstOption = this.strapOptions[0];
        const firstOptionPrice = this.parsePositiveInt(firstOption?.price, 0);
        if (firstOptionPrice > 0) return firstOptionPrice;

        return 0;
      }

      getSelectedHeadBasePriceCents() {
        const productBasePrice = this.getSelectedProductBasePriceCents();
        const mandatoryStrapBasePrice = this.getMandatoryStrapBasePriceCents();

        if (productBasePrice > 0 && mandatoryStrapBasePrice > 0) {
          return Math.max(0, productBasePrice - mandatoryStrapBasePrice);
        }

        if (this.headBasePriceFallbackCents > 0) {
          return this.headBasePriceFallbackCents;
        }

        return productBasePrice;
      }

      getHeadUnitPriceAtPosition(positionIndex, basePriceCents) {
        if (positionIndex === 1) return basePriceCents;
        if (positionIndex === 2) return Math.min(basePriceCents, this.headSecondPriceCents);
        if (positionIndex === 3) return Math.min(basePriceCents, this.headThirdPriceCents);
        return Math.min(basePriceCents, this.headFourthPlusPriceCents);
      }

      getStrapUnitPriceAtPosition(positionIndex, basePriceCents) {
        if (positionIndex <= 1) return Math.min(basePriceCents, this.strapFirstPriceCents);
        return Math.min(basePriceCents, this.strapSecondPlusPriceCents);
      }

      renderPriceWithCompare(target, discountedCents, originalCents) {
        if (!target) return;

        const normalizedDiscounted = Math.max(0, this.parsePositiveInt(discountedCents, 0));
        const normalizedOriginal = Math.max(normalizedDiscounted, this.parsePositiveInt(originalCents, normalizedDiscounted));
        const discountPercent =
          normalizedOriginal > 0 ? Math.max(0, Math.round(((normalizedOriginal - normalizedDiscounted) / normalizedOriginal) * 100)) : 0;

        if (normalizedDiscounted < normalizedOriginal) {
          const percentMarkup =
            discountPercent > 0 ? `<span class="watch-configurator__price-percent">-${discountPercent}%</span>` : '';
          target.innerHTML = `
            <s class="watch-configurator__price-original">${this.formatMoney(normalizedOriginal)}</s>
            <span class="watch-configurator__price-discounted">${this.formatMoney(normalizedDiscounted)}</span>
            ${percentMarkup}
          `;
          return;
        }

        target.innerHTML = `<span class="watch-configurator__price-current">${this.formatMoney(normalizedDiscounted)}</span>`;
      }

      refreshDisplayedPrices() {
        const rows = this.getRowElements();
        const headBasePriceCents = this.getSelectedHeadBasePriceCents();

        rows.forEach((rowElement, rowIndex) => {
          const rowPosition = rowIndex + 1;
          const headDiscounted = this.getHeadUnitPriceAtPosition(rowPosition, headBasePriceCents);
          const rowHeadPriceElement = rowElement.querySelector('[data-watch-row-head-price]');
          const rowMetaElement = rowElement.querySelector('[data-watch-row-meta]');
          if (rowHeadPriceElement) {
            if (rowPosition === 1) {
              rowHeadPriceElement.classList.add('is-hidden');
              rowHeadPriceElement.innerHTML = '';
              rowMetaElement?.classList.add('is-hidden');
            } else {
              rowHeadPriceElement.classList.remove('is-hidden');
              this.renderPriceWithCompare(rowHeadPriceElement, headDiscounted, headBasePriceCents);
              rowMetaElement?.classList.remove('is-hidden');
            }
          }

          const optionPriceElements = Array.from(rowElement.querySelectorAll('[data-watch-strap-option-price]'));
          optionPriceElements.forEach((priceElement) => {
            const strapBasePrice = this.parsePositiveInt(priceElement.dataset.variantPrice, 0);
            const strapDiscountedPrice = this.getStrapUnitPriceAtPosition(rowPosition, strapBasePrice);
            this.renderPriceWithCompare(priceElement, strapDiscountedPrice, strapBasePrice);
          });
        });

        const extraPriceElements = Array.from(this.querySelectorAll('[data-watch-extra-price]'));
        extraPriceElements.forEach((priceElement) => {
          const strapBasePrice = this.parsePositiveInt(priceElement.dataset.variantPrice, 0);
          const strapDiscountedPrice = this.getStrapUnitPriceAtPosition(2, strapBasePrice);
          this.renderPriceWithCompare(priceElement, strapDiscountedPrice, strapBasePrice);
        });
      }

      computeHeadTotalCents(headCount) {
        const basePriceCents = this.getSelectedHeadBasePriceCents();

        let total = 0;
        for (let index = 1; index <= headCount; index += 1) {
          total += this.getHeadUnitPriceAtPosition(index, basePriceCents);
        }

        return total;
      }

      computeStrapTotalCents(rows, extras) {
        const unitPrices = [];

        rows.forEach((row) => {
          if (!row.strapVariantId) return;
          const priceCents = this.strapPriceByVariantId[row.strapVariantId] ?? 0;
          unitPrices.push(priceCents);
        });

        extras.forEach((extra) => {
          const priceCents = this.strapPriceByVariantId[extra.variantId] ?? 0;
          for (let index = 0; index < extra.quantity; index += 1) {
            unitPrices.push(priceCents);
          }
        });

        let total = 0;
        unitPrices.forEach((basePriceCents, unitIndex) => {
          total += this.getStrapUnitPriceAtPosition(unitIndex + 1, basePriceCents);
        });

        return total;
      }

      updateEstimate() {
        if (!this.estimateValue) return;

        const rows = this.collectRows();
        const extras = this.collectExtraStraps();

        const headTotalCents = this.computeHeadTotalCents(rows.length);
        const strapTotalCents = this.computeStrapTotalCents(rows, extras);

        this.estimateValue.textContent = this.formatMoney(headTotalCents + strapTotalCents);
      }

      formatMoney(amountCents) {
        const locale = document.documentElement.lang || 'en';

        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: this.currencyCode,
          }).format(amountCents / 100);
        } catch (error) {
          return `${(amountCents / 100).toFixed(2)} ${this.currencyCode}`;
        }
      }

      drawPreview() {
        const activeRow = this.getRowElements().find((rowElement) => rowElement.dataset.rowId === this.activeRowId);
        const activeTimeInput = activeRow?.querySelector('[data-watch-row-time]');
        const activeTextInput = activeRow?.querySelector('[data-watch-row-text]');
        this.currentTime = this.normalizeTime(activeTimeInput?.value || this.defaultTime);
        const currentText = this.normalizeDialText(activeTextInput?.value || '');

        if (this.canvas) {
          this.drawPreviewCanvas(this.canvas, this.currentTime, currentText);
        }
        this.drawAllRowPreviews();
      }

      drawAllRowPreviews() {
        this.getRowElements().forEach((rowElement) => {
          const previewCanvas = rowElement.querySelector('[data-watch-row-canvas]');
          if (!previewCanvas) return;

          const timeInput = rowElement.querySelector('[data-watch-row-time]');
          const textInput = rowElement.querySelector('[data-watch-row-text]');
          const rowTime = this.normalizeTime(timeInput?.value || this.defaultTime);
          const rowText = this.normalizeDialText(textInput?.value || '');
          this.drawPreviewCanvas(previewCanvas, rowTime, rowText);
        });
      }

      drawPreviewCanvas(canvasElement, time24h, dialText = '') {
        if (!canvasElement) return;
        const context = canvasElement.getContext('2d');
        if (!context) return;

        const width = canvasElement.clientWidth || Number.parseInt(canvasElement.getAttribute('width') || '340', 10);
        const aspectRatioFallback = this.canvasConfig?.dataset.imageAspectRatio || '1';
        const fallbackHeight =
          Number.parseInt(canvasElement.getAttribute('height') || '', 10) ||
          Math.round(width / this.parsePositiveFloat(aspectRatioFallback, 1));
        const height = canvasElement.clientHeight || fallbackHeight;
        const ratio = window.devicePixelRatio || 1;

        canvasElement.width = Math.round(width * ratio);
        canvasElement.height = Math.round(height * ratio);
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        context.clearRect(0, 0, width, height);

        this.drawBaseLayer(context, width, height);
        this.drawHandsLayer(context, width, height, time24h, dialText);
      }

      drawBaseLayer(context, width, height) {
        if (this.dialImage) {
          if (this.previewMode === 'full') {
            this.drawImageContain(context, this.dialImage, width, height);
          } else {
            this.drawImageCover(context, this.dialImage, width, height);
          }
          return;
        }

        context.fillStyle = '#f4f4f4';
        context.fillRect(0, 0, width, height);
        context.strokeStyle = '#d9d9d9';
        context.lineWidth = 2;
        context.beginPath();
        context.arc(width / 2, height / 2, width * 0.45, 0, Math.PI * 2);
        context.stroke();
      }

      drawHandsLayer(context, width, height, time24h, dialText = '') {
        const normalizedText = this.normalizeDialText(dialText);
        if (normalizedText) {
          const dialReferenceSizeForText = Math.min(width, height);
          const centerXForText = (width * Number.parseFloat(this.canvasConfig?.dataset.centerXPercent || '50')) / 100;
          const centerYForText = (height * Number.parseFloat(this.canvasConfig?.dataset.centerYPercent || '50')) / 100;
          this.drawDialTextLayer(context, centerXForText, centerYForText, dialReferenceSizeForText, normalizedText);
          return;
        }

        const [hourValueRaw, minuteValueRaw] = this.normalizeTime(time24h).split(':');
        const hours = Number.parseInt(hourValueRaw || '0', 10);
        const minutes = Number.parseInt(minuteValueRaw || '0', 10);
        const dialReferenceSize = Math.min(width, height);

        const centerX = (width * Number.parseFloat(this.canvasConfig?.dataset.centerXPercent || '50')) / 100;
        const centerY = (height * Number.parseFloat(this.canvasConfig?.dataset.centerYPercent || '50')) / 100;
        const hourLength =
          (dialReferenceSize * Number.parseFloat(this.canvasConfig?.dataset.hourHandLengthPercent || '36')) / 100;
        const minuteLength =
          (dialReferenceSize * Number.parseFloat(this.canvasConfig?.dataset.minuteHandLengthPercent || '44')) / 100;

        const handWidthPercent = this.parsePositiveFloat(this.canvasConfig?.dataset.handWidthPercent, 4.5);
        const handWidth = Math.max(1, (dialReferenceSize * handWidthPercent) / 100);

        const hourAngle = ((hours % 12) + minutes / 60) * 30;
        const minuteAngle = minutes * 6;

        this.drawHand(context, {
          centerX,
          centerY,
          length: hourLength,
          angleDegrees: hourAngle,
          width: handWidth,
          color: this.canvasConfig?.dataset.hourHandColor || '#1a1a1a',
        });

        this.drawHand(context, {
          centerX,
          centerY,
          length: minuteLength,
          angleDegrees: minuteAngle,
          width: handWidth,
          color: this.canvasConfig?.dataset.minuteHandColor || '#1a1a1a',
        });

        context.fillStyle = this.canvasConfig?.dataset.hourHandColor || '#1a1a1a';
        context.beginPath();
        context.arc(centerX, centerY, Math.max(3, handWidth * 0.32), 0, Math.PI * 2);
        context.fill();
      }

      drawDialTextLayer(context, centerX, centerY, dialReferenceSize, dialText) {
        const value = this.normalizeDialText(dialText);
        if (!value) return;

        const rawFontSize = (dialReferenceSize * this.dialTextSizePercent) / 100 + 2;
        const fontSize = Math.max(11, Math.round(rawFontSize * 0.9));
        context.save();
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillStyle = this.canvasConfig?.dataset.hourHandColor || '#1a1a1a';
        context.shadowColor = '#ffffff';
        context.shadowBlur = 1;
        context.shadowOffsetX = 0;
        context.shadowOffsetY = 0;
        context.font = `600 ${fontSize}px Calibri, Arial, sans-serif`;
        context.fillText(value, centerX, centerY);
        context.restore();
      }

      drawImageContain(context, image, width, height) {
        const sourceWidth = image.naturalWidth || image.width;
        const sourceHeight = image.naturalHeight || image.height;
        if (!sourceWidth || !sourceHeight) return;

        const scale = Math.min(width / sourceWidth, height / sourceHeight);
        const drawWidth = sourceWidth * scale;
        const drawHeight = sourceHeight * scale;
        const dx = (width - drawWidth) / 2;
        const dy = (height - drawHeight) / 2;
        context.drawImage(image, dx, dy, drawWidth, drawHeight);
      }

      drawImageCover(context, image, width, height) {
        const sourceWidth = image.naturalWidth || image.width;
        const sourceHeight = image.naturalHeight || image.height;
        if (!sourceWidth || !sourceHeight) return;

        const scale = Math.max(width / sourceWidth, height / sourceHeight);
        const drawWidth = sourceWidth * scale;
        const drawHeight = sourceHeight * scale;
        const dx = (width - drawWidth) / 2;
        const dy = (height - drawHeight) / 2;
        context.drawImage(image, dx, dy, drawWidth, drawHeight);
      }

      drawHand(context, { centerX, centerY, length, angleDegrees, width, color }) {
        const radians = ((angleDegrees - 90) * Math.PI) / 180;
        const x2 = centerX + Math.cos(radians) * length;
        const y2 = centerY + Math.sin(radians) * length;

        context.save();
        context.shadowColor = '#ffffff';
        context.shadowBlur = 1;
        context.shadowOffsetX = 0;
        context.shadowOffsetY = 0;
        context.beginPath();
        context.moveTo(centerX, centerY);
        context.lineTo(x2, y2);
        context.strokeStyle = color;
        context.lineWidth = width;
        context.lineCap = 'round';
        context.stroke();
        context.restore();
      }
    }
  );
}
